import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import {
    ArrowClockwise,
    BarChartFill,
    CheckCircleFill,
    ClipboardCheckFill,
    DatabaseFill,
    ExclamationTriangleFill,
    FileEarmarkCode,
    FileEarmarkTextFill,
    PlayCircleFill,
} from 'react-bootstrap-icons';

import { fetchProjectAnalytics } from '../../api/projects';
import type {
    ProjectAnalyticsJobTypeBreakdown,
    ProjectAnalyticsResponse,
} from '../../types/project';
import './ProjectAnalyticsTab.css';

interface ProjectAnalyticsTabProps {
    projectId: number;
    projectName?: string | null;
}

function formatNumber(value?: number | null): string {
    return Number(value || 0).toLocaleString();
}

function countPhrase(value: number, singular: string, plural?: string): string {
    return `${formatNumber(value)} ${value === 1 ? singular : (plural || `${singular}s`)}`;
}

function formatPercent(value?: number | null): string {
    const numeric = Number(value || 0);
    return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}%`;
}

function formatDate(value?: string | null): string {
    if (!value) return 'No runs yet';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'No runs yet';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function normalizeStatusLabel(value?: string | null): string {
    if (!value) return 'None';
    return value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function safeErrorMessage(error: unknown): string {
    const anyError = error as { response?: { data?: { detail?: unknown } }; message?: string };
    const detail = anyError?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    return anyError?.message || 'Failed to load project analytics.';
}

function KpiTile({
    icon,
    label,
    value,
    helper,
    tone,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
    helper: string;
    tone: 'blue' | 'green' | 'amber' | 'rose';
}): React.ReactElement {
    return (
        <div className={`pa-kpi pa-kpi--${tone}`}>
            <div className="pa-kpi-icon">{icon}</div>
            <div className="pa-kpi-copy">
                <span className="pa-kpi-label">{label}</span>
                <strong>{value}</strong>
                <span>{helper}</span>
            </div>
        </div>
    );
}

function SectionHeader({
    icon,
    title,
    subtitle,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
}): React.ReactElement {
    return (
        <div className="pa-section-header">
            <span className="pa-section-icon">{icon}</span>
            <div>
                <h3>{title}</h3>
                <p>{subtitle}</p>
            </div>
        </div>
    );
}

function StatRow({ label, value }: { label: string; value: string | number }): React.ReactElement {
    return (
        <div className="pa-stat-row">
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}

function StatusPill({ active, trueLabel, falseLabel }: { active: boolean; trueLabel: string; falseLabel: string }): React.ReactElement {
    return (
        <span className={`pa-status-pill ${active ? 'pa-status-pill--ok' : 'pa-status-pill--muted'}`}>
            {active ? <CheckCircleFill size={13} /> : <ExclamationTriangleFill size={13} />}
            {active ? trueLabel : falseLabel}
        </span>
    );
}

function JobBreakdownRow({ row }: { row: ProjectAnalyticsJobTypeBreakdown }): React.ReactElement {
    return (
        <div className="pa-job-row">
            <div>
                <strong>{row.label}</strong>
                <span>{formatNumber(row.total)} total</span>
            </div>
            <div className="pa-job-statuses">
                <span className="pa-mini-pill pa-mini-pill--green">{formatNumber(row.passed)} success</span>
                <span className="pa-mini-pill pa-mini-pill--rose">{formatNumber(row.failed)} failed</span>
                <span className="pa-mini-pill pa-mini-pill--slate">{formatNumber(row.cancelled)} cancelled</span>
                <span className="pa-mini-pill pa-mini-pill--amber">{formatNumber(row.active)} active</span>
            </div>
        </div>
    );
}

function EmptyState({ title, text }: { title: string; text: string }): React.ReactElement {
    return (
        <div className="pa-empty">
            <BarChartFill size={24} />
            <strong>{title}</strong>
            <span>{text}</span>
        </div>
    );
}

function ProjectAnalyticsTab({ projectId, projectName }: ProjectAnalyticsTabProps): React.ReactElement {
    const [analytics, setAnalytics] = useState<ProjectAnalyticsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadAnalytics = useCallback(async (): Promise<void> => {
        if (!Number.isFinite(projectId)) return;
        setLoading(true);
        setError(null);
        try {
            setAnalytics(await fetchProjectAnalytics(projectId));
        } catch (err) {
            setError(safeErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        void loadAnalytics();
    }, [loadAnalytics]);

    const latestRunText = useMemo(
        () => formatDate(analytics?.playwright.latest_execution_at ?? null),
        [analytics?.playwright.latest_execution_at],
    );

    if (loading && !analytics) {
        return (
            <div className="pa-loading">
                <Spinner animation="border" size="sm" />
                <span>Loading analytics...</span>
            </div>
        );
    }

    if (error && !analytics) {
        return (
            <div className="pa-page">
                <Alert variant="danger" className="pa-alert">{error}</Alert>
                <button type="button" className="pa-refresh-btn" onClick={() => void loadAnalytics()}>
                    <ArrowClockwise size={14} />
                    Retry
                </button>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="pa-page">
                <EmptyState title="No analytics available" text="Analytics will appear after project activity is recorded." />
            </div>
        );
    }

    const linkedTestCases = analytics.test_cases.test_cases_with_source_links || 0;
    const unlinkedTestCases = Math.max((analytics.test_cases.total_test_cases || 0) - linkedTestCases, 0);
    const testGenerationRuns = analytics.jobs.by_type.find((row) => row.job_type === 'testcases')?.total || 0;
    const hasTestGenerationContext = testGenerationRuns > 0 || linkedTestCases > 0 || analytics.documents.latest_job_selected_document_ids.length > 0;
    const latestRequirementDocCount = Math.max(
        analytics.documents.latest_job_selected_requirement_documents_count ?? 0,
        analytics.documents.latest_job_selected_document_ids.length,
    );
    const uploadedDomainDocs = analytics.documents.domain_docs_uploaded || 0;
    const uploadedEnterpriseDocs = analytics.documents.enterprise_docs_uploaded || 0;
    const latestDomainDocCount = hasTestGenerationContext
        ? Math.max(analytics.documents.latest_job_domain_documents_count ?? 0, uploadedDomainDocs)
        : 0;
    const latestEnterpriseDocCount = hasTestGenerationContext
        ? Math.max(analytics.documents.latest_job_enterprise_documents_count ?? 0, uploadedEnterpriseDocs)
        : 0;
    const backendKnowledgeDocsUsed =
        (analytics.documents.domain_documents_used_in_jobs ?? 0) +
        (analytics.documents.enterprise_documents_used_in_jobs ?? 0);
    const knowledgeDocsUsed = hasTestGenerationContext
        ? Math.max(backendKnowledgeDocsUsed, uploadedDomainDocs + uploadedEnterpriseDocs)
        : backendKnowledgeDocsUsed;
    const documentsUsedByJobs =
        (analytics.documents.documents_used_in_jobs || 0) +
        Math.max(knowledgeDocsUsed - backendKnowledgeDocsUsed, 0);
    const uploadedButUnusedDocuments = Math.max((analytics.documents.total_docs_uploaded || 0) - documentsUsedByJobs, 0);
    const requirementDocJobs = Math.max(
        analytics.documents.test_generation_jobs_with_requirement_docs ?? 0,
        latestRequirementDocCount > 0 ? 1 : 0,
    );
    const domainContextJobs = Math.max(
        analytics.documents.test_generation_jobs_with_domain_docs ?? 0,
        hasTestGenerationContext && uploadedDomainDocs > 0 ? Math.max(testGenerationRuns, 1) : 0,
    );
    const enterpriseContextJobs = Math.max(
        analytics.documents.test_generation_jobs_with_enterprise_docs ?? 0,
        hasTestGenerationContext && uploadedEnterpriseDocs > 0 ? Math.max(testGenerationRuns, 1) : 0,
    );
    const latestContextText = [
        countPhrase(latestRequirementDocCount, 'requirement doc'),
        latestDomainDocCount > 0 ? countPhrase(latestDomainDocCount, 'domain doc') : 'no domain docs',
        latestEnterpriseDocCount > 0 ? countPhrase(latestEnterpriseDocCount, 'enterprise doc') : 'no enterprise docs',
    ].join(', ');
    const jobContextText = [
        `${formatNumber(requirementDocJobs)} req-doc jobs`,
        `${formatNumber(domainContextJobs)} domain-context jobs`,
        `${formatNumber(enterpriseContextJobs)} enterprise-context jobs`,
    ].join(', ');

    return (
        <div className="pa-page">
            <div className="pa-toolbar">
                <div>
                    <span className="pa-eyebrow">Project Analytics</span>
                    <h2>{projectName || 'Project'}</h2>
                    <p>Coverage, generation, execution, and sync health from persisted project data.</p>
                </div>
                <button type="button" className="pa-refresh-btn" onClick={() => void loadAnalytics()} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <ArrowClockwise size={14} />}
                    Refresh
                </button>
            </div>

            {error && <Alert variant="warning" className="pa-alert">{error}</Alert>}
            {analytics.warnings.length > 0 && (
                <Alert variant="warning" className="pa-alert">
                    {analytics.warnings[0]}
                </Alert>
            )}

            <div className="pa-kpi-grid">
                <KpiTile
                    icon={<ClipboardCheckFill />}
                    label="User Story Coverage"
                    value={formatPercent(analytics.requirements.coverage_percent)}
                    helper={`${formatNumber(analytics.requirements.covered_user_stories)} of ${formatNumber(analytics.requirements.user_stories_total)} covered`}
                    tone="blue"
                />
                <KpiTile
                    icon={<FileEarmarkTextFill />}
                    label="Test Cases"
                    value={formatNumber(analytics.test_cases.total_test_cases)}
                    helper={`${formatNumber(analytics.test_cases.test_cases_with_source_links)} with source links`}
                    tone="green"
                />
                <KpiTile
                    icon={<BarChartFill />}
                    label="Job Runs"
                    value={formatNumber(analytics.jobs.total_job_runs)}
                    helper={`${formatNumber(analytics.jobs.failed_job_runs)} failed, ${formatNumber(analytics.jobs.active_job_runs)} active`}
                    tone="amber"
                />
                <KpiTile
                    icon={<PlayCircleFill />}
                    label="Playwright Pass Rate"
                    value={formatPercent(analytics.playwright.pass_rate)}
                    helper={latestRunText}
                    tone="rose"
                />
            </div>

            <div className="pa-grid">
                <section className="pa-panel">
                    <SectionHeader
                        icon={<ClipboardCheckFill />}
                        title="QA Coverage"
                        subtitle="User-story coverage and traceability from generated test cases."
                    />
                    <div className="pa-progress">
                        <span style={{ width: `${Math.min(Math.max(analytics.requirements.coverage_percent, 0), 100)}%` }} />
                    </div>
                    <div className="pa-stat-grid">
                        <StatRow label="Epics" value={formatNumber(analytics.requirements.epics_total)} />
                        <StatRow label="User Stories" value={formatNumber(analytics.requirements.user_stories_total)} />
                        <StatRow label="Covered Stories" value={formatNumber(analytics.requirements.covered_user_stories)} />
                        <StatRow label="Uncovered Stories" value={formatNumber(analytics.requirements.uncovered_user_stories)} />
                        <StatRow label="Linked Test Cases" value={formatNumber(linkedTestCases)} />
                        <StatRow label="Unlinked Test Cases" value={formatNumber(unlinkedTestCases)} />
                    </div>
                    {analytics.requirements.user_stories_total === 0 ? (
                        <EmptyState title="No user stories found" text="Generate requirements or sync Azure DevOps work items to populate coverage analytics." />
                    ) : analytics.requirements.sample_uncovered_user_stories.length > 0 ? (
                        <div className="pa-list-block">
                            <span className="pa-list-title">Sample uncovered stories</span>
                            {analytics.requirements.sample_uncovered_user_stories.map((story) => (
                                <div className="pa-story-row" key={story.id}>
                                    <strong>#{story.id}</strong>
                                    <span>{story.title || 'Untitled user story'}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState title="No uncovered stories" text="Every current user story has at least one linked test case." />
                    )}
                </section>

                <section className="pa-panel">
                    <SectionHeader
                        icon={<FileEarmarkTextFill />}
                        title="Document Usage"
                        subtitle="Requirement source documents and project knowledge context used by generation jobs."
                    />
                    <div className="pa-stat-grid">
                        <StatRow label="Total Uploaded" value={formatNumber(analytics.documents.total_docs_uploaded)} />
                        <StatRow label="Requirement Docs" value={formatNumber(analytics.documents.requirement_docs_uploaded)} />
                        <StatRow label="Domain Context Docs" value={formatNumber(analytics.documents.domain_docs_uploaded)} />
                        <StatRow label="Enterprise Context Docs" value={formatNumber(analytics.documents.enterprise_docs_uploaded)} />
                        <StatRow label="Used By Jobs" value={formatNumber(documentsUsedByJobs)} />
                        <StatRow label="Uploaded But Unused" value={formatNumber(uploadedButUnusedDocuments)} />
                    </div>
                    <div className="pa-id-strip">
                        <span>Latest generation context</span>
                        <strong>{latestContextText}</strong>
                    </div>
                    <div className="pa-id-strip">
                        <span>Test generation job coverage</span>
                        <strong>{jobContextText}</strong>
                    </div>
                </section>

                <section className="pa-panel pa-panel--wide">
                    <SectionHeader
                        icon={<BarChartFill />}
                        title="Job Runs"
                        subtitle="Status distribution across generation, execution, Gherkin, synthetic, and validation jobs."
                    />
                    <div className="pa-status-grid">
                        <span><strong>{formatNumber(analytics.jobs.passed_job_runs)}</strong> Success</span>
                        <span><strong>{formatNumber(analytics.jobs.failed_job_runs)}</strong> Failed</span>
                        <span><strong>{formatNumber(analytics.jobs.cancelled_job_runs)}</strong> Cancelled</span>
                        <span><strong>{formatNumber(analytics.jobs.active_job_runs)}</strong> Active</span>
                    </div>
                    {analytics.jobs.by_type.length > 0 ? (
                        <div className="pa-job-list">
                            {analytics.jobs.by_type.map((row) => <JobBreakdownRow key={row.job_type} row={row} />)}
                        </div>
                    ) : (
                        <EmptyState title="No job runs yet" text="Generation and execution jobs will appear here after they are started." />
                    )}
                </section>

                <section className="pa-panel">
                    <SectionHeader
                        icon={<PlayCircleFill />}
                        title="Playwright Execution"
                        subtitle="Execution coverage and pass/fail totals from run summaries."
                    />
                    <div className="pa-stat-grid">
                        <StatRow label="Execution Runs" value={formatNumber(analytics.playwright.execution_runs_total)} />
                        <StatRow label="Distinct Tests Executed" value={formatNumber(analytics.playwright.distinct_test_cases_executed)} />
                        <StatRow label="Result Entries" value={formatNumber(analytics.playwright.test_result_entries_total)} />
                        <StatRow label="Passed" value={formatNumber(analytics.playwright.passed_test_cases)} />
                        <StatRow label="Failed" value={formatNumber(analytics.playwright.failed_test_cases)} />
                        <StatRow label="Latest Status" value={normalizeStatusLabel(analytics.playwright.latest_execution_status)} />
                    </div>
                    {analytics.playwright.variant_results_total > 0 && (
                        <div className="pa-id-strip">
                            <span>Synthetic variants</span>
                            <strong>
                                {formatNumber(analytics.playwright.passed_variant_results)} passed / {formatNumber(analytics.playwright.failed_variant_results)} failed
                            </strong>
                        </div>
                    )}
                </section>

                <section className="pa-panel">
                    <SectionHeader
                        icon={<DatabaseFill />}
                        title="Generated Assets"
                        subtitle="Gherkin and synthetic data availability for the project."
                    />
                    <div className="pa-asset-stack">
                        <div className="pa-asset-row">
                            <FileEarmarkCode />
                            <div>
                                <strong>Gherkin / BDD</strong>
                                <span>{formatNumber(analytics.gherkin.artifacts_count)} artifacts from {formatNumber(analytics.gherkin.jobs_count)} jobs</span>
                            </div>
                            <StatusPill active={analytics.gherkin.generated} trueLabel="Generated" falseLabel="Not generated" />
                        </div>
                        <div className="pa-asset-row">
                            <DatabaseFill />
                            <div>
                                <strong>Synthetic Data</strong>
                                <span>{formatNumber(analytics.synthetic_data.test_cases_with_generated_data)} test cases, {formatNumber(analytics.synthetic_data.total_generated_rows)} rows</span>
                            </div>
                            <StatusPill active={analytics.synthetic_data.generated} trueLabel="Generated" falseLabel="Not generated" />
                        </div>
                    </div>
                </section>

                <section className="pa-panel pa-panel--wide">
                    <SectionHeader
                        icon={<CheckCircleFill />}
                        title="Azure DevOps"
                        subtitle="Requirement and execution sync counts kept separate from local AcceliQA counts."
                    />
                    <div className="pa-stat-grid pa-stat-grid--six">
                        <StatRow label="Plan Mapped" value={analytics.azure_devops.test_plan_mapped ? 'Yes' : 'No'} />
                        <StatRow label="Suite Mappings" value={formatNumber(analytics.azure_devops.suite_mappings_count)} />
                        <StatRow label="Test Cases Pushed" value={formatNumber(analytics.azure_devops.test_cases_pushed_to_ado)} />
                        <StatRow label="Linked Epics" value={formatNumber(analytics.azure_devops.linked_epics)} />
                        <StatRow label="Linked User Stories" value={formatNumber(analytics.azure_devops.linked_user_stories)} />
                        <StatRow label="Linked Tasks" value={formatNumber(analytics.azure_devops.linked_tasks)} />
                        <StatRow label="Push Jobs" value={formatNumber(analytics.azure_devops.requirement_push_jobs)} />
                        <StatRow label="Push Failures" value={formatNumber(analytics.azure_devops.requirement_push_failed_count)} />
                        <StatRow label="Execution Sync Runs" value={formatNumber(analytics.azure_devops.playwright_result_sync_runs)} />
                        <StatRow label="Results Synced" value={formatNumber(analytics.azure_devops.playwright_results_synced)} />
                        <StatRow label="Results Skipped" value={formatNumber(analytics.azure_devops.playwright_results_skipped)} />
                        <StatRow label="Results Failed" value={formatNumber(analytics.azure_devops.playwright_results_failed)} />
                    </div>
                </section>
            </div>

            <div className="pa-updated">
                Generated {formatDate(analytics.generated_at)}
            </div>
        </div>
    );
}

export default ProjectAnalyticsTab;
